import container from "@src/container";
import { getConnectionProviderLastRunQueryKey } from "@queries/connection";
import { IdpTypes, StaticProviderDetails } from "@services/integrations";

export const isStaticIntegration = (idp) =>
  [IdpTypes.CLASSLINK, IdpTypes.CLEVER].includes(idp?.typeId);

/**
 * Whether the customer-facing rostering on/off toggle should be shown for an org.
 *
 * Mirrors the gating used by the client-portal edit-org modal: the org must have
 * integrations enabled, an IDP selected, and at least one external integration id.
 *
 * @param {object} org - Organization details (from useOrganizationDetails).
 * @returns {boolean}
 */
export const canShowRosterSync = (org) =>
  !!org?.integrations &&
  !!org?.orgIdp?.id &&
  (org?.orgExternalIds?.length ?? 0) > 0;

export const getStaticIntegrationContext = (currentUser, providerList) => {
  const hasStaticConnection = isStaticIntegration(currentUser?.idp);

  const providerDetails = hasStaticConnection
    ? StaticProviderDetails[currentUser?.idp?.typeId]
    : undefined;

  if (providerDetails && !providerDetails.providerId) {
    providerDetails.providerId = providerList.find(
      (p) => p.name === providerDetails.providerName
    )?.id;
  }

  return { hasStaticConnection, providerDetails };
};

export const buildStaticConnectionFromLastRun = (
  lastRun,
  providerDetails,
  idOverride
) => {
  if (!lastRun || !providerDetails) {
    return undefined;
  }

  return {
    id: idOverride ?? lastRun.connectionId,
    providerId: providerDetails.providerId,
    providerName: providerDetails.providerName,
    createdDate: lastRun.executionDateTime || null,
    lastRunDate: lastRun.executionDateTime || null,
    lastRunSyncJobId: lastRun.jobExecutionId || null,
    lastRunStatus: lastRun.status || null,
  };
};

export async function buildConnectionRowsForOrg(
  org,
  providerList = [],
  currentUser,
  preFetchedLastRun = undefined,
  queryClient = undefined
) {
  const { integrationService } = container;

  const { hasStaticConnection, providerDetails } = getStaticIntegrationContext(
    currentUser,
    providerList
  );

  try {
    if (hasStaticConnection && providerDetails) {
      // Use pre-fetched data if available (null means no last run, undefined means not fetched)
      // If undefined, fetch individually
      let lastRun;
      if (preFetchedLastRun !== undefined) {
        lastRun = preFetchedLastRun;
      } else {
        // Use React Query's fetchQuery to leverage caching and shared query key logic
        const queryKey = getConnectionProviderLastRunQueryKey(
          providerDetails.providerName,
          org.code
        );
        const lastRunMapping = await queryClient.fetchQuery({
          queryKey,
          queryFn: () =>
            integrationService.getConnectionProviderLastRun(
              providerDetails.providerName,
              [org.code]
            ),
        });
        lastRun = lastRunMapping?.[org.code] ?? null;
      }

      const staticRow = buildStaticConnectionFromLastRun(
        lastRun,
        providerDetails
      );

      return staticRow
        ? [
            {
              ...staticRow,
              organizationCode: org.code,
              organizationName: org.name,
              isStatic: true,
            },
          ]
        : [];
    }

    const dynamicConnections = await integrationService.getConnectionsList(
      org.code
    );
    return (dynamicConnections || []).map((c) => ({
      ...c,
      organizationCode: org.code,
      organizationName: org.name,
      isStatic: false,
    }));
  } catch (error) {
    // Log error but return empty array so other orgs can still succeed
    console.error(
      `[Integrations] Failed to build connection rows for org ${org.code}:`,
      error
    );
    return [];
  }
}
